import React, { useId } from 'react';
import { LOGO_H, LOGO_W, LogoMaskDefs } from './LogoMaskDefs';
import { siteConfig } from '../../data/siteConfig';
interface LogoMark3DProps {
  className?: string;
  /** Number of extrusion layers between front and back faces. */
  depth?: number;
  /** Distance in px between extrusion layers. */
  step?: number;
  /** CSS perspective distance in px — smaller values exaggerate depth. */
  perspective?: number;
  shadow?: boolean;
}
const FRONT: [number, number, number] = [166, 132, 76];
const BACK: [number, number, number] = [70, 52, 26];
function layerColor(t: number) {
  const c = FRONT.map((f, i) => Math.round(f + (BACK[i] - f) * t));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

/**
 * Extruded 3D logo built from the original asset. The motion cycle
 * (entrance → zoom → 360° Y rotation → light sweep → float/rest, 10s loop)
 * lives in index.css under `.q3d-*` and is disabled for reduced motion.
 */
export function LogoMark3D({
  className = '',
  depth = 10,
  step = 0.9,
  perspective = 1000,
  shadow = false
}: LogoMark3DProps) {
  const id = 'q3' + useId().replace(/[^a-zA-Z0-9]/g, '');
  const half = depth * step / 2;
  const viewBox = `0 0 ${LOGO_W} ${LOGO_H}`;
  const mask = `url(#${id}-mask)`;
  return <div className={`q3d-scene relative ${className}`} style={{
    perspective: `${perspective}px`
  }} aria-hidden="true" data-id="element-162" data-mp="2zbz0620vdpf4">
      <LogoMaskDefs id={id} data-id="element-163" data-mp="2v8aujovst3wi" />
      <div className="q3d-enter absolute inset-0" data-id="element-164" data-mp="3s0wj87cwnq8x">
        <div className="q3d-float absolute inset-0" data-id="element-165" data-mp="3rzldw33taafp">
          <div className="q3d-spin absolute inset-0" data-id="element-166" data-mp="3n11uoaglrcr1">
            {/* Back face — reads correctly when the mark turns away */}
            <div className="q3d-face absolute inset-0" style={{
            transform: `translateZ(${-half}px) rotateY(180deg)`
          }} data-id="element-167" data-mp="3iy53vwgwsac0">
              <svg viewBox={viewBox} className="h-full w-full" data-id="element-168" data-mp="3e2psdb6diqir">
                <g mask={mask} data-id="element-169" data-mp="27lb2izyen267">
                  <image href={siteConfig.logoUrl} width={LOGO_W} height={LOGO_H} preserveAspectRatio="xMidYMid meet" data-id="element-170" data-mp="27igivan7tldu" />
                  <rect width={LOGO_W} height={LOGO_H} fill="#000" opacity="0.18" data-id="element-171" data-mp="242lyuyojffew" />
                </g>
              </svg>
            </div>

            {/* Extruded edge */}
            {Array.from({
            length: depth - 1
          }, (_, i) => {
            const z = half - (i + 1) * step;
            return <div key={i} className="absolute inset-0" style={{
              transform: `translateZ(${z}px)`
            }} data-id="element-172" data-mp="1z394lsvc762a">
                  <svg viewBox={viewBox} className="h-full w-full" data-id="element-173" data-mp="1lcdz8qk1r73g">
                    <rect width={LOGO_W} height={LOGO_H} fill={layerColor((i + 1) / depth)} mask={mask} data-id="element-174" data-mp="1la1ek5mwmimp" />
                  </svg>
                </div>;
          })}

            {/* Front face with metallic light sweep */}
            <div className="q3d-face absolute inset-0" style={{
            transform: `translateZ(${half}px)`,
            filter: shadow ? 'drop-shadow(0 16px 24px rgba(0, 0, 0, 0.45))' : undefined
          }} data-id="element-175" data-mp="1gchyd06nbvs4">
              <svg viewBox={viewBox} className="h-full w-full" data-id="element-176" data-mp="17e5y06fz9jmh">
                <g mask={mask} data-id="element-177" data-mp="nr325jthlkxw">
                  <image href={siteConfig.logoUrl} width={LOGO_W} height={LOGO_H} preserveAspectRatio="xMidYMid meet" data-id="element-178" data-mp="nse4rgb74mnf" />
                  <polygon className="q3d-sheen" points="0,-30 120,-30 60,330 -60,330" fill={`url(#${id}-sheen)`} style={{
                  mixBlendMode: 'screen'
                }} data-id="element-179" data-mp="wqzi8x1t9bhc" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>;
}